// src/components/PrivacyPolicy.jsx
import {
  Box,
  Container,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Text from 'commons/Text';

import useStyles from './style';

const PrivacyPolicy = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const title = t('privacyPolicy.title');
  const intro1 = t('privacyPolicy.intro1');
  const intro2 = t('privacyPolicy.intro2');
  const updated = t('privacyPolicy.updated');
  const sections = t('privacyPolicy.sections', { returnObjects: true });

  return (
    <Box className={classes.wrapper}>
      <Container disableGutters>
        <Box className={classes.section}>
          <Text className={classes.heading}>{title}</Text>
          <Text className={classes.paragraph}>{intro1}</Text>
          <Text className={classes.paragraph}>{intro2}</Text>
          <Text className={classes.subtitle}>{updated}</Text>

          {sections.map((sec, idx) => (
            <Box
              key={idx}
              className={classes.section}
            >
              {/* Section heading */}
              <Text className={classes.subtitle}>
                <strong>{sec.heading}</strong>
              </Text>

              {/* Any leading paragraphs */}
              {sec.content?.map((p, i) => (
                <Text
                  key={i}
                  className={classes.paragraph}
                >
                  {p}
                </Text>
              ))}

              {/* Top‑level list */}
              {sec.list && (
                <Box
                  component="ul"
                  className={classes.list}
                >
                  {sec.list.map((item, j) => (
                    <Box
                      key={j}
                      component="li"
                      className={classes.listItem}
                    >
                      <Text className={classes.textStyle1}>{item}</Text>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Top‑level note */}
              {sec.note?.map((note, n) => (
                <Text
                  key={n}
                  className={classes.paragraph}
                >
                  {note}
                </Text>
              ))}

              {/* Subsections */}
              {sec.subsections?.map((sub, k) => (
                <Box
                  key={k}
                  className={classes.section}
                >
                  <Text className={classes.subtitle}>
                    <strong>{sub.heading}</strong>
                  </Text>

                  {sub.content?.map((p2, m) => (
                    <Text
                      key={m}
                      className={classes.paragraph}
                    >
                      {p2}
                    </Text>
                  ))}

                  {/* Sub-image */}
                  {sub.image && (
                    <Box className={classes.imageContainer}>
                      <img
                        src={sub.image}
                        alt={sub.heading}
                        className={classes.image}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          marginTop: '16px',
                          marginBottom: '16px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}
                      />
                    </Box>
                  )}

                  {/* Sub-list */}
                  {sub.list && (
                    <Box
                      component="ul"
                      className={classes.list}
                    >
                      {sub.list.map((item2, n) => (
                        <Box
                          key={n}
                          component="li"
                          className={classes.listItem}
                        >
                          <Text className={classes.textStyle1}>{item2}</Text>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Sub-note */}
                  {sub.note?.map((note2, nn) => (
                    <Text
                      key={nn}
                      className={classes.paragraph}
                    >
                      {note2}
                    </Text>
                  ))}

                  {/* Sub-table */}
                  {sub.table && (
                    <TableContainer
                      component={Paper}
                      className={classes.tableContainer}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {sub.table.columns.map((col, cidx) => (
                              <TableCell key={cidx}>
                                <strong>{col}</strong>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sub.table.rows.map((row, ridx) => (
                            <TableRow key={ridx}>
                              {row.map((cell, c) => (
                                <TableCell key={c}>{cell}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
